import { useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "../../lib/api.js";
import type { TenantIntegrationKind } from "../../lib/integrationKinds.js";
import { testHeadline, type AiProviderTestResponse } from "../../lib/aiProvider.js";

/**
 * Inline test modal for an integration (ADR-0085 Phase 2 §5.7).
 *
 * Replaces the throwaway DevPlexTester page from Phase 1. For `plex_api`
 * integrations the modal shows datasource_id + inputs JSON, executes the
 * test endpoint, and renders the cleaned-up error message with a "Show raw
 * response" disclosure that surfaces the parsed Plex error envelope.
 */

interface ExecuteSuccess {
  ok: true;
  result: {
    columns: string[];
    rows: unknown[][];
    rowLimitedExceeded: boolean;
    transactionNo: string;
    durationMs: number;
  };
}

interface ExecuteFailure {
  ok: false;
  errorCode: string;
  message: string;
  details?: unknown;
}

type ExecuteResponse = ExecuteSuccess | ExecuteFailure;

export function IntegrationTestModal(props: {
  integrationId: string;
  integrationName: string;
  kind: TenantIntegrationKind;
  onClose: () => void;
}): JSX.Element {
  const [datasourceId, setDatasourceId] = useState<string>("");
  const [inputsJson, setInputsJson] = useState<string>("{}");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ExecuteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  if (props.kind === "ai_provider") {
    return <AiProviderTestModal integrationId={props.integrationId} onClose={props.onClose} />;
  }

  if (props.kind !== "plex_api") {
    return (
      <div className="modalOverlay" role="presentation" onClick={props.onClose}>
        <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: 16 }}>Test affordance for kind &quot;{props.kind}&quot; not implemented.</div>
        </div>
      </div>
    );
  }

  async function runTest() {
    setBusy(true);
    setError(null);
    setResult(null);
    setShowRaw(false);

    const dsId = Number(datasourceId);
    if (!Number.isInteger(dsId) || dsId <= 0) {
      setError("Datasource ID must be a positive integer.");
      setBusy(false);
      return;
    }

    let inputs: Record<string, unknown>;
    try {
      const parsed = JSON.parse(inputsJson);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setError("Inputs must be a JSON object.");
        setBusy(false);
        return;
      }
      inputs = parsed as Record<string, unknown>;
    } catch {
      setError("Inputs is not valid JSON.");
      setBusy(false);
      return;
    }

    try {
      const res = await apiFetch<ExecuteResponse>(
        `/api/tenant/admin/integrations/${props.integrationId}/test`,
        {
          method: "POST",
          body: { datasource_id: dsId, inputs }
        }
      );
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modalOverlay" role="presentation" onClick={props.onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 640, maxWidth: "calc(100vw - 32px)" }}
      >
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong>Test connection — {props.integrationName}</strong>
            <button type="button" className="btn" onClick={props.onClose}>
              Close
            </button>
          </div>

          <label className="field">
            <span className="label">Datasource ID</span>
            <input
              className="input"
              type="number"
              value={datasourceId}
              onChange={(e) => setDatasourceId(e.target.value)}
              placeholder="1791"
            />
          </label>

          <label className="field">
            <span className="label">Inputs (JSON)</span>
            <textarea
              className="input"
              rows={4}
              value={inputsJson}
              onChange={(e) => setInputsJson(e.target.value)}
              style={{ fontFamily: "monospace" }}
            />
            <div className="help">
              The contents of the <code>inputs</code> map only. Atlas wraps this as{" "}
              <code>{`{ "inputs": <your-object> }`}</code> before sending to Plex.
              Example: <code>{`{"Part_No":"1234"}`}</code> — not the full envelope.
            </div>
          </label>

          <div className="actions">
            <button
              type="button"
              className="btn btnPrimary"
              onClick={() => void runTest()}
              disabled={busy}
            >
              {busy ? "Running…" : "Execute"}
            </button>
          </div>

          {error ? (
            <div className="card" style={{ borderColor: "var(--danger-text)" }}>
              <div style={{ color: "var(--danger-text)" }}>{error}</div>
            </div>
          ) : null}

          {result ? (
            <div className="card">
              {result.ok ? (
                <div style={{ display: "grid", gap: 8 }}>
                  <div>
                    <span className="pill pillOk">success</span>{" "}
                    <span className="muted">
                      {result.result.rows.length} row{result.result.rows.length === 1 ? "" : "s"} ·{" "}
                      {result.result.durationMs} ms · txn {result.result.transactionNo || "—"}
                    </span>
                  </div>
                  {result.result.rowLimitedExceeded ? (
                    <div className="pill pillWarn">Plex truncated the result.</div>
                  ) : null}
                  {result.result.columns.length > 0 ? (
                    <div className="tableWrap" style={{ maxHeight: 280, overflow: "auto" }}>
                      <table className="table">
                        <thead>
                          <tr>
                            {result.result.columns.map((c) => (
                              <th key={c}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.result.rows.slice(0, 50).map((row, i) => (
                            <tr key={i}>
                              {row.map((v, j) => (
                                <td key={j} className="mono">{String(v ?? "")}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  <div>
                    <span className="pill pillBad">{result.errorCode}</span>
                  </div>
                  <div>{result.message}</div>
                  {result.details !== undefined ? (
                    <details>
                      <summary style={{ cursor: "pointer" }}>
                        {showRaw ? "Hide raw response" : "Show raw response"}
                      </summary>
                      <pre
                        className="mono"
                        style={{ whiteSpace: "pre-wrap", overflow: "auto", maxHeight: 200 }}
                        onClick={() => setShowRaw((v) => !v)}
                      >
                        {JSON.stringify(result.details, null, 2)}
                      </pre>
                    </details>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * ADR-0207 §3.2 — the AI provider's test: one tiny request per model the
 * connection serves, run as soon as the modal opens. A row per model; a
 * failure names the step and the provider's own status.
 */
function AiProviderTestModal(props: { integrationId: string; onClose: () => void }): JSX.Element {
  const [result, setResult] = useState<AiProviderTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setResult(
          await apiFetch<AiProviderTestResponse>(`/api/tenant/admin/integrations/${props.integrationId}/test`, { method: "POST", body: {} })
        );
      } catch (e) {
        setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
  }, [props.integrationId]);

  return (
    <div className="modalOverlay" role="presentation" onClick={props.onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxWidth: "calc(100vw - 32px)" }}
      >
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong>Test connection — AI provider</strong>
            <button type="button" className="btn" onClick={props.onClose}>
              Close
            </button>
          </div>
          {error ? (
            <div style={{ color: "var(--danger-text)" }}>{error}</div>
          ) : !result ? (
            <div className="muted">Testing…</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              <div>
                <span className={`pill ${result.ok ? "pillOk" : "pillBad"}`}>{result.ok ? "Working" : "Failing"}</span>{" "}
                {testHeadline(result)}
              </div>
              {result.models.length > 0 ? (
                <div className="tableWrap">
                  <table className="table tableCompact">
                    <tbody>
                      {result.models.map((m) => (
                        <tr key={m.model_id}>
                          <td className="mono">{m.model_id}</td>
                          <td>{m.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
